import {
  getAuthHeaders,
  runBulkWithSimulatedProgress,
  performRequestTypeGET,
  performRequestTypePOST,
  performRequestTypePATCH,
  performRequestTypeDELETE,
  performRequestTypeMultipartPOST,
  fetchPresignedDownloadUrl,
  getReferenceFallbackUrl,
  getEvidenceFallbackUrl
} from "../utils/servicesUtils";
import { startLiveProgressListener } from "./webSocketServices";

// Controlled access to obligation hierarchy based on user permissions
export const getObligationStructures = ({ signal } = {}) =>
  performRequestTypeGET("/api/obligation/structures/lite/", { signal, errorMessage: "Failed to load records" });

export const getMyObligationsByFramework = (framework_id) =>
  performRequestTypeGET(`/api/obligation/my/groups/by-framework/${encodeURIComponent(framework_id)}`, { errorMessage: "Failed to load records" });

export const getMyRecurrencesByObligation = (group_id) =>
  performRequestTypeGET(`/api/obligation/my/obligations/by-group/${encodeURIComponent(group_id)}`, { errorMessage: "Failed to load records" });

export const getObligationGroupDetails = (group_id) =>
  performRequestTypeGET(`/api/obligation/${encodeURIComponent(group_id)}/details/`, { errorMessage: "Failed to get details" });

/**
 * Lightweight obligation/group details for headers, sidebars, and batched hydration.
 *
 * @param {string} group_id - Obligation group id (path segment).
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] - Optional. Files header hydration passes this through its
 *   scheduler so scrolled-away cards can cancel both queued and in-flight lite requests.
 * @returns {Promise<object>} Parsed JSON body.
 */
export const getObligationGroupDetailsLite = (group_id, { signal } = {}) =>
  performRequestTypeGET(`/api/obligation/lite/${encodeURIComponent(group_id)}/`, { signal, errorMessage: "Failed to get details" });

export const getRecurrenceDetails = (obligation_id) =>
  performRequestTypeGET(`/api/obligation/deep/${encodeURIComponent(obligation_id)}/details/`, { errorMessage: "Failed to get details" });

// expected body structure:
// {
//   "group_ids": [
//      "id1",
//      "id2",
//   ]
// }
export const getObligationGroupsStatusCounts = (body) =>
  performRequestTypePOST("/api/obligation/groups/status-counts/", { body });

// expected body structure:
// {
//   "user_id": "5eb7cf5a86d9755df3a6c593",
//   "department_id": "5eb7cf5a86d9755df3a6c593",
//   "start": "2026-01-21T21:22:05.064Z",
//   "end": "2026-01-21T21:22:05.064Z",
//   "scope": "single"
// }
export const reassignObligationRecurrence = (obligation_id, body) =>
  performRequestTypePOST(`/api/obligation/${encodeURIComponent(obligation_id)}/reassign/`, { body });

export const removeSecondaryUsersFromObligationRecurrence = (obligation_id, body) =>
  performRequestTypePATCH(`/api/obligation/${encodeURIComponent(obligation_id)}/secondary-responsibles/remove/`, { body });

// expected body structure:
// {
//   "notes": [
//     "string"
//   ]
// }
export const postObligationRecurrenceNotes = (obligation_id, body) =>
  performRequestTypePOST(`/api/obligation/${encodeURIComponent(obligation_id)}/notes/`, { body });

export const deleteObligationRecurrenceNote = (obligation_id, note_id) =>
  performRequestTypeDELETE(`/api/obligation/${encodeURIComponent(obligation_id)}/notes/${encodeURIComponent(note_id)}/`);

/* ================================ REFERENCE MANAGEMENT ================================= */

export const downloadObligationReferenceDocument = (document_id) =>
  fetchPresignedDownloadUrl(`/api/obligation/references/documents/${encodeURIComponent(document_id)}/download/`, {
    idField: "document_id",
    idValue: document_id,
    errorMessage: "Failed to download file",
    fallbackUrl: getReferenceFallbackUrl,
  });

/**
 * Upload reference files for a single obligation
 * @param {string} group_id - Obligation group ID
 * @param {File[]} files - Files to upload
 * @param {Object} tags - Optional tags
 * @returns {Promise} API response
 */
export const uploadReferenceFiles = async (group_id, files, tags = null) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(group_id)}/references/documents/upload/`;

  const formData = new FormData();

  Array.from(files).forEach((file) => {
    formData.append("files", file);
  });

  if (tags) formData.append("tags", JSON.stringify(tags));

  return performRequestTypeMultipartPOST(ENDPOINT, { formData });
};

export const deleteReferenceFiles = (group_id, reference_id) =>
  performRequestTypeDELETE(`/api/obligation/${encodeURIComponent(group_id)}/references/documents/${encodeURIComponent(reference_id)}/`);

/**
 * Post reference text for a single obligation
 * @param {string} group_id - Obligation group ID
 * @param {Object} body - POST body object, expects
 * {
 *    "text": "string"
 * }
 * @returns {Promise} API response
 */
export const postReferenceText = (group_id, body) =>
  performRequestTypePOST(`/api/obligation/${encodeURIComponent(group_id)}/references/text/`, { body });

export const deleteReferenceText = (group_id, reference_id) =>
  performRequestTypeDELETE(`/api/obligation/${encodeURIComponent(group_id)}/references/text/${encodeURIComponent(reference_id)}/`);

/* ================================= EVIDENCE MANAGEMENT ================================= */

/**
 * Returns presigned download URL + MIME for a recurrence evidence document (GET; JSON body).
 * Used by Files V2 `FilePreview`, obligation file widgets (`useFileData`), etc.
 *
 * @param {string} evidence_id - Evidence document id.
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] - Optional. Pass from `FilePreview` / `AbortController` to cancel
 *   when the preview card unmounts or the user scrolls away — frees browser connections and avoids work
 *   for off-screen cards. Existing callers that omit `options` behave unchanged (`signal` is undefined).
 * @returns {Promise<{ evidence_id?: string, download_url: string|null, mime_type: string|null, error: Error|null }>}
 */
export const downloadRecurrenceEvidenceDocument = (evidence_id, { signal } = {}) =>
  fetchPresignedDownloadUrl(`/api/obligation/evidence/${encodeURIComponent(evidence_id)}/download/`, {
    idField: "evidence_id",
    idValue: evidence_id,
    signal,
    errorMessage: "Failed to download file",
    fallbackUrl: getEvidenceFallbackUrl,
  });

/**
 * Get presigned URL for a criteria PDF by obj_key.
 * Uses GET /api/obligation/pdf/criteria/presigned-url/?obj_key=...
 * Used by Files V2 preview when the file list has criteria_pdf with obj_key from POST /api/files/.
 *
 * @param {string} obj_key - Storage object key for the criteria PDF.
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] - Optional. Pass from `FilePreview` to cancel in-flight request on
 *   unmount/scroll. Omitted by legacy callers — behavior unchanged.
 * @returns {Promise<{ download_url: string|null, mime_type: string, error: Error|null }>}
 */
export const getCriteriaPdfPresignedUrl = async (obj_key, { signal } = {}) => {
  if (!obj_key) {
    return { download_url: null, mime_type: "application/pdf", error: new Error("obj_key is required") };
  }

  return fetchPresignedDownloadUrl(`/api/obligation/pdf/criteria/presigned-url/?obj_key=${encodeURIComponent(obj_key)}`, {
    signal,
    errorMessage: "Failed to get criteria PDF URL",
  });
};

export const downloadCriteriaPdfDocument = (obligation_id) =>
  fetchPresignedDownloadUrl(`/api/obligation/pdf/criteria/${encodeURIComponent(obligation_id)}/`, {
    idField: "obligation_id",
    idValue: obligation_id,
    errorMessage: "Failed to download criteria pdf",
  });

// expected assignments structure:
// [
//   {
//     "file_index": 0,
//     "criterion_ids": ["345137092ebb43d8bbe046641509e4ca"]
//   }
// ]
export const uploadEvidenceFiles = async (obligation_id, files, assignments) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/evidence/files/`;

  const formData = new FormData();

  Array.from(files).forEach((file) => {
    formData.append("files", file);
  });

  formData.append("assignments", JSON.stringify(assignments));

  return performRequestTypeMultipartPOST(ENDPOINT, { formData });
};

// expected body structure:
// {
//   "criterion_ids": [
//     "string"
//   ],
//   "delete_if_orphan": false
// }
export const deleteEvidenceFiles = (obligation_id, evidence_id, body) =>
  performRequestTypeDELETE(`/api/obligation/${encodeURIComponent(obligation_id)}/criteria/evidence/${encodeURIComponent(evidence_id)}/`, { body });

// expected body structure:
// [
//   {
//     "content": "declarative",
//     "criterion_ids": [
//       "345137092ebb43d8bbe046641509e4ca"
//     ]
//   }
// ]
export const addEvidenceDeclarative = (obligation_id, body) =>
  performRequestTypePOST(`/api/obligation/declarative/${encodeURIComponent(obligation_id)}/criteria/batch/`, { body });

// expected body structure:
// {
//   "criterion_ids": [
//     "string"
//   ],
// }
export const deleteEvidenceDeclarative = (obligation_id, declarative_id, body) =>
  performRequestTypeDELETE(`/api/obligation/${encodeURIComponent(obligation_id)}/criteria/declarative/${encodeURIComponent(declarative_id)}/`, { body });

// expected body structure:
// [
//   {
//     "content": "link",
//     "criterion_ids": [
//       "345137092ebb43d8bbe046641509e4ca"
//     ]
//   }
// ]
export const addExternalLinks = (obligation_id, body) =>
  performRequestTypePOST(`/api/obligation/${encodeURIComponent(obligation_id)}/criteria/comments/batch/`, { body });

// expected body structure:
// {
//   "criterion_ids": [
//     "string"
//   ],
//   "delete_if_orphan": false
// }
export const deleteExternalLinks = (obligation_id, comment_id, body) =>
  performRequestTypeDELETE(`/api/obligation/${encodeURIComponent(obligation_id)}/criteria/comments/${encodeURIComponent(comment_id)}/`, { body });

// expected body structure:
// {
//   "assignments": [
//     {
//       "criterion_ids": [
//         "345137092ebb43d8bbe046641509e4ca"
//       ],
//       "obligation_ids": [
//         "696541c9093c2467d6ef95a7"
//       ]
//     }
//   ]
// }
export const linkObligationToCriteria = (obligation_id, body) =>
  performRequestTypePATCH(`/api/obligation/${encodeURIComponent(obligation_id)}/criteria/link-obligations/`, { body });

// expected body structure:
// {
//   "criterion_ids": [
//     "string"
//   ]
// }
export const removeObligationLinkFromCriteria = (obligation_id, linked_obligation_id, body) =>
  performRequestTypeDELETE(`/api/obligation/${encodeURIComponent(obligation_id)}/criteria/${encodeURIComponent(linked_obligation_id)}/unlink/`, { body });

// expected body structure:
// {
//   "evidence_type": "evidence" | "comment" | "declarative",
//   "target_id": "string" - id of the evidence that is being checked
//   "criterion_ids": [
//     "string"
//   ]
// }
export const getEvidenceDeletionPermissions = (obligation_id, body) =>
  performRequestTypePOST(`/api/obligation/${encodeURIComponent(obligation_id)}/criteria/evidence/can-delete/`, { body });

// expected body structure (use formdata):
/* ================================ RECURRENCE MANAGEMENT ================================ */
// expected body structure:
// {
//   "completed_date": "2026-01-15T22:48:37.537Z"
// }
export const completeRecurrence = (obligation_id, body) =>
  performRequestTypePATCH(`/api/obligation/${encodeURIComponent(obligation_id)}/complete/`, { body });

export const undoRecurrenceCompletion = (obligation_id) =>
  performRequestTypePATCH(`/api/obligation/reopen/${encodeURIComponent(obligation_id)}/`);

/* ============================ BULK SERVICES FOR OBLIGATIONS ============================ */
export const bulkAssignResponsible = async (body = {}, onProgress = () => { }) => {
  const ENDPOINT = "/api/obligation/bulk-assign";
  const request = {
    url: ENDPOINT,
    options: {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }
  };

  const result = await startLiveProgressListener(request, onProgress);
  return result;
};

/**
 * Simulates progress updates for bulk assignment
 * This is a temporary solution until websocket/SSE is implemented
 */
export const bulkAssignResponsibleV2 = async (body = {}, onProgress = () => { }) => {
  return runBulkWithSimulatedProgress({
    endpoint: "/api/obligation/bulk-assign",
    fetchOptions: {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    },
    itemCount: body.group_ids?.length,
    onProgress,
    errorLabel: "Bulk assign responsible error",
  });
};

/**
 * Upload reference files to multiple obligations in bulk with progress tracking
 * @param {Array} files - Array of File objects
 * @param {Object} payload - Object containing group_ids as an array, it has to be defined as:
 * {
 *    group_ids: ["string", "string"]
 * }
 * @param {string} tags - Tags of the files
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} Summary of results
 */
export const bulkUploadReferenceFiles = async (
  files = [],
  payload,
  tags,
  onProgress = () => { },
) => {
  const ENDPOINT = "/api/obligation/upload/documents/bulk/reference/";
  const headers = getAuthHeaders();

  // For formData, remove Content-Type to let the browser set the correct boundary.
  delete headers["Content-Type"];

  // Multipart payload expected by the server
  const formData = new FormData();

  Array.from(files).forEach((file) => {
    formData.append("files", file);
  });

  formData.append("payload", JSON.stringify(payload));

  if (tags) formData.append("tags", JSON.stringify(tags));

  const request = {
    url: ENDPOINT,
    options: {
      method: "POST",
      headers,
      body: formData,
    }
  };

  const result = await startLiveProgressListener(request, onProgress);
  return result;
};

export const bulkUploadReferenceFilesV2 = async (
  files = [],
  payload,
  tags,
  onProgress = () => { },
) => {
  const headers = getAuthHeaders();

  // For formData, remove Content-Type to let the browser set the correct boundary.
  delete headers["Content-Type"];

  // Multipart payload expected by the server
  const formData = new FormData();

  Array.from(files).forEach((file) => {
    formData.append("files", file);
  });

  formData.append("payload", JSON.stringify(payload));

  if (tags) formData.append("tags", JSON.stringify(tags));

  return runBulkWithSimulatedProgress({
    endpoint: "/api/obligation/upload/documents/bulk/reference/",
    fetchOptions: {
      method: "POST",
      headers,
      body: formData,
    },
    itemCount: payload.group_ids?.length,
    onProgress,
    errorLabel: "Bulk upload reference files error",
  });
};

/**
 * Post reference text to multiple obligations in bulk with progress tracking
 * @param {Object} body - Object with group IDs and reference text
 *
  expected body:
  {
    "group_ids": [
      "string"
    ],
    "text": "string"
  }
*
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} Summary of results
 */
export const bulkPostReferenceText = async (
  body = {},
  onProgress = () => { },
) => {
  const ENDPOINT = "/api/obligation/references/text/bulk/";
  const request = {
    url: ENDPOINT,
    options: {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }
  };

  const result = await startLiveProgressListener(request, onProgress);
  return result;
};

export const bulkPostReferenceTextV2 = async (
  payload,
  onProgress = () => { },
) => {
  const itemCount = payload.group_ids?.length || 1;
  const textLength = payload.text?.length || 0;

  // Base: 100ms per item, +50ms per 100 characters
  const baseTime = itemCount * 100;
  const textTime = (textLength / 100) * 50;
  const estimatedDuration = Math.min(Math.max(baseTime + textTime, 2000), 10000);

  return runBulkWithSimulatedProgress({
    endpoint: "/api/obligation/references/text/bulk/",
    fetchOptions: {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    },
    estimatedDuration,
    onProgress,
    errorLabel: "Bulk post reference text error",
  });
};

/**
 * Post reference text to multiple obligations in bulk with progress tracking
 * @param {Object} body - Object with group IDs and reference text
 *
  expected body:
  {
    "group_ids": [
      "string"
    ],
    "kind": "string" - text | document
  }
*
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} Summary of results
 */
export const bulkDeleteReferences = async (
  body,
  onProgress = () => { },
) => {
  const ENDPOINT = "/api/obligation/references/bulk/delete/";
  const request = {
    url: ENDPOINT,
    options: {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }
  };

  const result = await startLiveProgressListener(request, onProgress);
  return result;
};

export const bulkDeleteReferencesV2 = async (
  payload,
  onProgress = () => { },
) => {
  return runBulkWithSimulatedProgress({
    endpoint: "/api/obligation/references/bulk/delete/",
    fetchOptions: {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    },
    itemCount: payload.group_ids?.length,
    onProgress,
    errorLabel: "Bulk delete references error",
  });
};

export const getAiObligationAnswer = async (payload, { signal } = {}) => {
  // Offline behavior:
  // when REACT_APP_MODE_OFFLINE is "true", skip the LLM call and return a generic message.
  const shouldSkipAICall = process.env.REACT_APP_MODE_OFFLINE === "true";
  if (shouldSkipAICall) {
    const language = (localStorage.getItem("i18nextLng") || "en").toLowerCase();
    const isSpanish = language.startsWith("es");

    if (isSpanish) {
      return {
        response: "Puede revisar los anexos de esta obligacion para obtener mas informacion, recuerde revisar la documentacion oficial, los archivos cargados como referencias contienen formatos o fuentes necesarios para el cumplimiento"
      };
    }

    return {
      response: "You can review this obligation's annexes for more information. Remember to review the official documentation; the files uploaded as references contain templates or sources required for compliance."
    };
  }

  const ENDPOINT = `/api/obligation/obligationai/`;
  const response = await fetch(ENDPOINT, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
    signal,
  });


  if (!response.ok) {
    let detail = "Failed to fetch IA obligation answer";
    try {
      const errorData = await response.json();
      detail = errorData?.detail || errorData?.message || detail;
    } catch {
      try {
        const text = await response.text();
        if (text) detail = text;
      } catch {
        // Keep default detail when response body can't be parsed.
      }
    }
    throw new Error(detail);
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return await response.json();
  }

  const text = await response.text();
  return { response: text };
};

/**
 * Like {@link getAiObligationAnswer} but sends the request as multipart/form-data
 * so an optional File can be attached alongside the standard payload fields.
 *
 * The payload fields (question, group_id, obligation_id) are
 * appended as plain form fields.
 * The file is appended under the `file` field when provided.
 */
export const postAiObligationFeedback = async (interaction_id, { helpful, comment = null }) => {
  const ENDPOINT = `/api/obligation/obligationai/${encodeURIComponent(interaction_id)}/feedback/`;
  const response = await fetch(ENDPOINT, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ helpful, comment }),
  });

  if (!response.ok) {
    let detail = "Failed to send feedback";
    try {
      const errorData = await response.json();
      detail = errorData?.detail || errorData?.message || detail;
    } catch {
      try {
        const text = await response.text();
        if (text) detail = text;
      } catch {
        // Keep default detail
      }
    }
    throw new Error(detail);
  }

  return await response.json();
};

export const getAiObligationAnswerWithFile = async (payload, file = null, { signal } = {}) => {
  const shouldSkipAICall = process.env.REACT_APP_MODE_OFFLINE === "true";
  if (shouldSkipAICall) {
    return getAiObligationAnswer(payload, { signal });
  }

  const ENDPOINT = `/api/obligation/obligationai/`;
  const formData = new FormData();
  formData.append("question", payload?.question ?? "");
  formData.append("group_id", payload?.group_id ?? "");
  formData.append("obligation_id", payload?.obligation_id ?? "");
  if (file) formData.append("file", file);

  const headers = getAuthHeaders();
  delete headers["Content-Type"];

  const response = await fetch(ENDPOINT, {
    method: "POST",
    headers,
    body: formData,
    signal,
  });

  if (!response.ok) {
    let detail = "Failed to fetch IA obligation answer";
    try {
      const errorData = await response.json();
      detail = errorData?.detail || errorData?.message || detail;
    } catch {
      try {
        const text = await response.text();
        if (text) detail = text;
      } catch {
        // Keep default detail when response body can't be parsed.
      }
    }
    throw new Error(detail);
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return await response.json();
  }

  const text = await response.text();
  return { response: text };
};